import { firstDayOfMonth, type SimpleDate, simplyToday } from '../utils/SimpleDate';


export interface SimpleOption {
	label: string;
	value: string;
}

export interface EWUser {
	username: string;
	email: string;
	canEdit: boolean;
	isSuperUser: boolean;
	version: string;
}

export interface Country {
	pk: number;
	name: string;
}

export enum Likelihood {
	unspecified = 0,
	negligible = 1,
	low = 25,
	moderate = 50,
	high = 75,
	veryHigh = 100,
}

export const likelihoodLabel = {
	[Likelihood.unspecified]: 'Unspecified',
	[Likelihood.negligible]: 'Negligible (< 5%)',
	[Likelihood.low]: 'Low (6% - 15%)',
	[Likelihood.moderate]: 'Moderate (16% - 30%)',
	[Likelihood.high]: 'High (31% - 50%)',
	[Likelihood.veryHigh]: 'Very high (51% - 100%)',
};

export enum Impact {
	unspecified = 0,
	negligible = 1,
	low = 25,
	moderate = 50,
	high = 75,
	veryHigh = 100,
}

export const impactLabel = {
	[Impact.unspecified]: 'Unspecified',
	[Impact.negligible]: 'Negligible (< 20,000)',
	[Impact.low]: 'Low (20,000 - 100,000)',
	[Impact.moderate]: 'Moderate (100,000 - 250,000)',
	[Impact.high]: 'High (250,000 - 500,000)',
	[Impact.veryHigh]: 'Very high (> 500,000)',
};

export enum Seriousness {
	notRelevant = -1,
	unspecified = 0,
	negligible = 1,
	low = 25,
	moderate = 50,
	high = 75,
	veryHigh = 100,
}

export const seriousnessLabel = {
	[Seriousness.notRelevant]: '',
	[Seriousness.unspecified]: 'Unspecified',
	[Seriousness.negligible]: 'Negligible',
	[Seriousness.low]: 'Low',
	[Seriousness.moderate]: 'Moderate',
	[Seriousness.high]: 'High',
	[Seriousness.veryHigh]: 'Very high',
};


// these colors also appears in RiskIcon.scss
export const seriousnessColor = {
	[Seriousness.notRelevant]: '#888',
	[Seriousness.unspecified]: '#dfe6eb',
	[Seriousness.negligible]: '#888',
	[Seriousness.low]: '#01b385',
	[Seriousness.moderate]: '#f7b824',
	[Seriousness.high]: '#f47846',
	[Seriousness.veryHigh]: '#d2121f',
};

export const seriousnessClass = {
	[Seriousness.notRelevant]: 's-not-relevant',
	[Seriousness.unspecified]: 's-none',
	[Seriousness.negligible]: 's-negligible',
	[Seriousness.low]: 's-low',
	[Seriousness.moderate]: 's-moderate',
	[Seriousness.high]: 's-high',
	[Seriousness.veryHigh]: 's-very-high',
};

export enum Risk {
	conflict = 'CONFLICT',
	economic = 'ECONOMIC',
	flooding = 'FLOODING',
	drought = 'DROUGHT',
	cyclones = 'CYCLONES',
	epidemic = 'EPIDEMIC',
	otherNH = 'OTHERNH',
	crossBorder = 'CROSSBORDER',
}

export const riskLabelDef = {  // default to be read from server
	[Risk.conflict]: '...',
	[Risk.economic]: '...',
	[Risk.flooding]: '...',
	[Risk.drought]: '...',
	[Risk.cyclones]: '...',
	[Risk.epidemic]: '...',
	[Risk.otherNH]: '...',
	[Risk.crossBorder]: '...',
};

export enum WarningEventType {
	raised = 10,
	updated = 20,
	closed = 30,
}

export const warningEventTypeLabel = {
	[WarningEventType.raised]: 'Raised',
	[WarningEventType.updated]: 'Updated',
	[WarningEventType.closed]: 'Closed',
};

export const warningEventTypeClass = {
	[WarningEventType.raised]: 'raised',
	[WarningEventType.updated]: 'updated',
	[WarningEventType.closed]: 'closed',
};

export enum WarningEventStatus {
	draft = 10,
	approved = 20,
	reopen = 30,
}

export const warningEventStatusLabel = {
	[WarningEventStatus.draft]: 'Draft',
	[WarningEventStatus.approved]: 'Approved',
	[WarningEventStatus.reopen]: 'Reopen',
};

export interface WarningEventComment {
	id: number;
	message: string;
	isOpen: boolean;
	lastUser: string;
	lastTimestamp: string | null;
}

export class WarningEvent {
	id = 0;
	date: SimpleDate = firstDayOfMonth(simplyToday());  // this month's first day
	type: WarningEventType = WarningEventType.raised;
	likelihood = Likelihood.unspecified;
	impact = Impact.unspecified;
	seriousness = Seriousness.unspecified;
	message = '';

	status = WarningEventStatus.draft;
	approvedOn: SimpleDate | null = null;

	comment: WarningEventComment | null = null;
}

export class FullWarningEvent extends WarningEvent {
	warning!: Warning;  // always received from /event/:id api + always init for new events
}

class WarningEventWithPartialWarning extends WarningEvent {
	warning?: Partial<Warning>;
}

export type PartialFullWarningEvent = Partial<WarningEventWithPartialWarning>;

const emptyEvent = new WarningEvent();

export class Warning {
	id = 0;
	title = '';
	rb = '';
	countries: Country[] = [];
	risks: Risk[] = [];
	description = '';
	keyTakeaway = '';
	background = '';
	outlook = '';
	indicators = '';
	raised = emptyEvent;
	closed: WarningEvent | null = null;
	latestEvent = emptyEvent;
}

export class WarningInCalendar extends Warning {
	// cells with value 'true' are those where a new event can be created
	// null are blocked cells (out of Raised-Closed range)
	eventsCalendar: (WarningEvent | true | null)[] = [];
}

export class FullWarning extends Warning {
	events: WarningEvent[] = [];
}

export interface RawCalendarData {
	warning: Warning;
	events: WarningEvent[];
}
